import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import PlanComparisonTable from "../components/PlanComparisonTable";
import { 
  Calendar, 
  Users, 
  Clock, 
  BarChart3, 
  CalendarDays,
  ChevronRight,
  Check,
  Play,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Menu,
  X,
  Loader2,
  LayoutDashboard,
  ExternalLink,
  ChevronDown,
  PartyPopper
} from "lucide-react";
import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";

// Rotalie Logo Component
const RotalieLogo = ({ className = "", size = "default" }) => {
  const sizes = {
    small: { icon: "w-8 h-8", check: "w-4 h-4", text: "text-lg" },
    default: { icon: "w-10 h-10", check: "w-5 h-5", text: "text-xl" },
    large: { icon: "w-14 h-14", check: "w-7 h-7", text: "text-2xl" }
  };
  const s = sizes[size] || sizes.default;
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className={`${s.icon} rounded-xl bg-gradient-to-br from-violet-500 to-teal-400 flex items-center justify-center relative`}>
        {/* Calendar tabs */}
        <div className="absolute -top-0.5 left-1/4 w-1.5 h-2 bg-gradient-to-b from-violet-500 to-violet-400 rounded-t-sm" />
        <div className="absolute -top-0.5 right-1/4 w-1.5 h-2 bg-gradient-to-b from-violet-500 to-violet-400 rounded-t-sm" />
        <Check className={`${s.check} text-white stroke-[3]`} />
      </div>
      <span className={`${s.text} font-bold text-slate-900 font-['Inter']`}>Rotalie</span>
    </div>
  );
};

// Logo for dark backgrounds
const RotalieLogoLight = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-teal-400 flex items-center justify-center relative">
      <div className="absolute -top-0.5 left-1/4 w-1.5 h-2 bg-gradient-to-b from-violet-400 to-violet-300 rounded-t-sm" />
      <div className="absolute -top-0.5 right-1/4 w-1.5 h-2 bg-gradient-to-b from-violet-400 to-violet-300 rounded-t-sm" />
      <Check className="w-5 h-5 text-white stroke-[3]" />
    </div>
    <span className="text-xl font-bold text-white font-['Inter']">Rotalie</span>
  </div>
);

const Landing = () => {
  const { isAuthenticated, isPlatformAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [currency, setCurrency] = useState("gbp");
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const geoRes = await api.get("/geolocation");
        const detectedCurrency = geoRes.data.currency || "gbp";
        setCurrency(detectedCurrency);
        setCurrencySymbol(geoRes.data.currency_symbol || "£");
        
        const packagesRes = await api.get(`/packages?currency=${detectedCurrency}`);
        setPackages(packagesRes.data.packages || []);
      } catch (error) {
        console.error("Error fetching packages:", error);
        setPackages([
          { id: "light_monthly", name: "Light", price: 9, tier: "light", type: "subscription", max_employees: 10, features: ["Up to 10 employees", "Day & week views", "Basic reports"] },
          { id: "standard_monthly", name: "Standard", price: 29, tier: "standard", type: "subscription", max_employees: 25, features: ["Up to 25 employees", "Cost tracking", "Break management", "Absence tracking"] },
          { id: "professional_monthly", name: "Professional", price: 59, tier: "professional", type: "subscription", max_employees: -1, features: ["Unlimited employees", "All features", "Priority support"] }
        ]);
      } finally {
        setLoadingPackages(false);
      }
    };
    init();
  }, []);

  const subscriptionPackages = packages.filter(p => p.type === "subscription");

  const features = [
    {
      icon: Calendar,
      title: "Intuitive Rota Builder",
      description: "Create schedules in minutes with our drag-and-drop interface. Switch between day and week views to plan your perfect rota. Click and drag directly on the timeline to create shifts instantly.",
      align: "left"
    },
    {
      icon: Users,
      title: "Employee Management",
      description: "Track pay rates, contracted hours, and manage your entire team from one central dashboard. Everything organized, nothing missed.",
      align: "right"
    },
    {
      icon: Clock,
      title: "Smart Break Management",
      description: "Configure break types with automatic calculations. Required breaks, paid or unpaid—all fully customizable to your needs.",
      align: "left"
    },
    {
      icon: BarChart3,
      title: "Real-Time Cost Tracking",
      description: "See exactly what your staff costs are as you build schedules. Export detailed reports in CSV or PDF format.",
      align: "right"
    },
    {
      icon: CalendarDays,
      title: "Absence Management",
      description: "Track holidays, sick leave, and other absences with automatic cost calculations for paid leave.",
      align: "left"
    },
    {
      icon: PartyPopper,
      title: "Events Management",
      description: "Plan staffing around parties, meetings, and special occasions. Events display on your timeline and track hours in reports.",
      align: "right"
    },
    {
      icon: Sparkles,
      title: "Flexible Pay Rates",
      description: "Handle overtime, weekend rates, and split shifts with ease. Set multiple pay rates per employee and watch costs calculate automatically.",
      align: "left"
    }
  ];

  const pricingDescriptions = {
    light: {
      description: "Perfect for small teams",
      features: [
        "Up to 10 employees",
        "Day & week rota views",
        "Basic reports",
        "Email support"
      ]
    },
    standard: {
      description: "For growing businesses",
      features: [
        "Up to 25 employees",
        "Advanced reports & exports",
        "Cost tracking",
        "Break management",
        "Absence tracking",
        "Events management",
        "Priority support"
      ],
      popular: true
    },
    professional: {
      description: "For larger teams",
      features: [
        "Unlimited employees",
        "Everything in Standard",
        "Multiple locations",
        "Dedicated support"
      ]
    }
  };

  const faqs = [
    {
      question: "How does the 30-day free trial work?",
      answer: "Your free trial gives you full access to all features for 30 days. No credit card required to start. At the end of your trial, you can choose a plan that fits your needs or your account will simply pause—no automatic charges."
    },
    {
      question: "Can I switch plans later?",
      answer: "Absolutely! You can upgrade or downgrade your plan at any time. If you upgrade, you'll get immediate access to additional features. If you downgrade, the change takes effect at the start of your next billing cycle."
    },
    {
      question: "How many employees can I add?",
      answer: "The Light plan supports up to 10 employees. Standard supports 25, and Professional offers unlimited employees. Need more? Add extra staff packs starting from £10/month."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, security is our top priority. We use industry-standard encryption (SSL/TLS) for all data transfers, and your data is stored securely with regular backups. We never share your data with third parties."
    },
    {
      question: "Can staff view their own schedules?",
      answer: "Yes! Staff members can log in using their company code and employee ID to view their upcoming shifts, request time off, and stay updated on schedule changes—all from any device."
    },
    {
      question: "Does Rotalie work on mobile devices?",
      answer: "Rotalie is fully responsive and works beautifully on smartphones, tablets, and desktops. Your team can check schedules, and managers can make quick adjustments from anywhere."
    },
    {
      question: "How do I handle different pay rates?",
      answer: "Rotalie supports multiple pay rates per employee. You can set up different rates for regular hours, overtime, weekends, nights, or any custom rate you need. Costs are calculated automatically."
    },
    {
      question: "Can I export my schedules and reports?",
      answer: "Yes, you can export schedules, timesheets, and cost reports in both CSV and PDF formats. This makes it easy to share with accountants, integrate with payroll systems, or keep records."
    }
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-20 backdrop-blur-xl bg-white/90 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            <Link to="/">
              <RotalieLogo />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Pricing</a>
              <Link to="/documentation" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">Docs</Link>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors font-medium">FAQ</a>
              {isAuthenticated ? (
                <a href={isPlatformAdmin ? "/admin" : "/dashboard"} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-full px-6 py-2 shadow-lg shadow-violet-500/25 transition-all duration-300 font-semibold flex items-center gap-2" data-testid="nav-dashboard-btn">
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </a>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 transition-colors font-medium" data-testid="nav-login-btn">
                    Sign In
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-full px-6 py-2.5 shadow-lg shadow-violet-500/25 transition-all duration-300 font-semibold" data-testid="nav-signup-btn">
                      Get started
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-white border-b border-slate-200 shadow-lg">
            <div className="px-6 py-4 space-y-4">
              <a href="#features" className="block text-slate-600 hover:text-slate-900 font-medium py-2">Features</a>
              <a href="#pricing" className="block text-slate-600 hover:text-slate-900 font-medium py-2">Pricing</a>
              <Link to="/documentation" className="block text-slate-600 hover:text-slate-900 font-medium py-2">Docs</Link>
              <a href="#faq" className="block text-slate-600 hover:text-slate-900 font-medium py-2">FAQ</a>
              {isAuthenticated ? (
                <a href={isPlatformAdmin ? "/admin" : "/dashboard"} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-gradient-to-r from-violet-500 to-teal-400 text-white rounded-full py-3 font-semibold">
                    Dashboard
                  </Button>
                </a>
              ) : (
                <>
                  <Link to="/login" className="block text-slate-600 hover:text-slate-900 font-medium py-2">Sign In</Link>
                  <Link to="/signup" className="block">
                    <Button className="w-full bg-gradient-to-r from-violet-500 to-teal-400 text-white rounded-full py-3 font-semibold">
                      Get started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-32 px-6 lg:px-8 relative overflow-hidden">
        {/* Background gradient wave */}
        <div className="absolute bottom-0 left-0 right-0 h-[60%] bg-gradient-to-br from-violet-50 via-white to-teal-50 -z-10" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Content */}
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900">
                Flexible staff scheduling,{" "}
                <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">
                  done right
                </span>
              </h1>
              
              <p className="text-lg lg:text-xl leading-relaxed text-slate-600 max-w-lg">
                Build rotas in minutes, track costs in real-time, and keep your whole team in sync. 
                The scheduling tool built for modern businesses.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button className="bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-violet-500/30 hover:shadow-violet-500/40 transition-all duration-300 font-semibold" data-testid="hero-cta-btn">
                    Get started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>

              <p className="text-sm text-slate-500 flex items-center gap-2">
                <Check className="w-4 h-4 text-teal-500" />
                30-day free trial • No credit card required
              </p>
            </div>

            {/* Right - App Preview */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-200/50 overflow-hidden">
                {/* Mock app interface */}
                <div className="h-full flex flex-col">
                  {/* App header */}
                  <div className="h-12 bg-slate-50 border-b border-slate-200 flex items-center px-4 gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 flex justify-center">
                      <div className="px-4 py-1 bg-white rounded-md text-xs text-slate-400 border border-slate-200">
                        rotalie.io/dashboard
                      </div>
                    </div>
                  </div>
                  
                  {/* App content - Rota grid */}
                  <div className="flex-1 p-4 bg-white">
                    <div className="h-full flex flex-col gap-3">
                      {/* Timeline header */}
                      <div className="flex items-center gap-2 text-xs text-slate-400 pl-20">
                        <span className="w-16 text-center">8am</span>
                        <span className="w-16 text-center">10am</span>
                        <span className="w-16 text-center">12pm</span>
                        <span className="w-16 text-center">2pm</span>
                        <span className="w-16 text-center">4pm</span>
                      </div>
                      
                      {/* Employee rows */}
                      {[
                        { name: "Sarah M.", shifts: [{ start: 0, width: 45, color: "bg-violet-400" }] },
                        { name: "James C.", shifts: [{ start: 25, width: 50, color: "bg-teal-400" }] },
                        { name: "Emma W.", shifts: [{ start: 10, width: 35, color: "bg-amber-400" }, { start: 55, width: 30, color: "bg-amber-400" }] },
                        { name: "Mike R.", shifts: [{ start: 0, width: 60, color: "bg-violet-400" }] }
                      ].map((employee, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-16 flex items-center gap-2 flex-shrink-0">
                            <div className={`w-6 h-6 rounded-full ${i % 2 === 0 ? 'bg-violet-100' : 'bg-teal-100'} flex items-center justify-center text-xs font-medium ${i % 2 === 0 ? 'text-violet-600' : 'text-teal-600'}`}>
                              {employee.name.charAt(0)}
                            </div>
                            <span className="text-xs text-slate-600 truncate">{employee.name}</span>
                          </div>
                          <div className="flex-1 h-8 bg-slate-50 rounded relative">
                            {employee.shifts.map((shift, j) => (
                              <div 
                                key={j}
                                className={`absolute top-1 bottom-1 ${shift.color} rounded-md opacity-90`}
                                style={{ left: `${shift.start}%`, width: `${shift.width}%` }}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-br from-violet-200 to-violet-100 rounded-2xl -z-10 blur-sm" />
              <div className="absolute -top-4 -right-4 w-28 h-28 bg-gradient-to-br from-teal-200 to-teal-100 rounded-2xl -z-10 blur-sm" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8" id="features">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-20 lg:mb-28">
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Everything you need to
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">manage your team</span>
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Powerful features that save you time and give you complete control over your staff scheduling.
            </p>
          </div>

          {/* Features - Alternating Layout */}
          <div className="space-y-24 lg:space-y-32">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  feature.align === 'right' ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={`space-y-6 ${feature.align === 'right' ? 'lg:order-2' : ''}`}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-teal-400 flex items-center justify-center">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900">
                    {feature.title}
                  </h3>
                  <p className="text-lg leading-relaxed text-slate-600">
                    {feature.description}
                  </p>
                  <Link to="/signup" className="inline-flex items-center text-violet-600 font-semibold hover:text-violet-700 transition-colors group">
                    Learn more 
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                {/* Video Placeholder */}
                <div className={`${feature.align === 'right' ? 'lg:order-1' : ''}`}>
                  <div className="aspect-video rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shadow-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-teal-400 flex items-center justify-center mx-auto mb-4 cursor-pointer hover:scale-105 transition-transform shadow-lg shadow-violet-500/30">
                        <Play className="w-7 h-7 text-white ml-1" />
                      </div>
                      <p className="text-slate-500 font-medium">Feature video</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-20 px-6 lg:px-8 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            {[
              { value: "5+", label: "Hours saved per week" },
              { value: "90%", label: "Fewer scheduling errors" },
              { value: "30", label: "Day free trial" },
              { value: "24/7", label: "Access anywhere" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-slate-600 mt-2 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8" id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Simple, transparent
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">pricing</span>
            </h2>
            <p className="text-lg lg:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Start free, upgrade when you are ready. All plans include a 30-day free trial.
            </p>
          </div>
          
          {loadingPackages ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {subscriptionPackages.map((pkg) => {
                const tierInfo = pricingDescriptions[pkg.tier] || {};
                const isPopular = tierInfo.popular || pkg.tier === "standard";
                return (
                  <div 
                    key={pkg.id} 
                    className={`relative rounded-2xl p-8 transition-all duration-300 ${
                      isPopular 
                        ? 'bg-gradient-to-br from-violet-500 to-teal-400 text-white shadow-xl shadow-violet-500/30 scale-105' 
                        : 'bg-white border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-1 bg-white text-violet-600 text-xs font-semibold rounded-full shadow-lg">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className={`text-lg font-semibold mb-1 ${isPopular ? 'text-white' : 'text-slate-900'}`}>{pkg.name}</h3>
                      <p className={`text-sm mb-4 ${isPopular ? 'text-white/80' : 'text-slate-500'}`}>{tierInfo.description}</p>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-4xl font-bold ${isPopular ? 'text-white' : 'text-slate-900'}`}>
                          {currencySymbol}{pkg.price}
                        </span>
                        <span className={isPopular ? 'text-white/70' : 'text-slate-500'}>/month</span>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {(tierInfo.features || pkg.features || []).map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 flex-shrink-0 ${isPopular ? 'text-white' : 'text-teal-500'}`} />
                          <span className={isPopular ? 'text-white/90' : 'text-slate-600'}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link to="/signup" className="block">
                      <Button 
                        className={`w-full rounded-full py-5 font-semibold transition-all duration-300 ${
                          isPopular 
                            ? 'bg-white hover:bg-slate-50 text-violet-600' 
                            : 'bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white shadow-lg shadow-violet-500/20'
                        }`}
                      >
                        Get started
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-center text-slate-500 mt-8 text-sm">
            Need more employees? Add extra staff packs from {currencySymbol}10/month
          </p>

          {/* Compare Features Button */}
          <div className="text-center mt-12">
            <Button
              variant="outline"
              onClick={() => setShowComparison(!showComparison)}
              className="rounded-full px-6 py-5 font-semibold border-slate-300 hover:border-violet-400 hover:bg-violet-50 transition-all duration-300"
            >
              {showComparison ? "Hide" : "Compare all features"}
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 ${showComparison ? "rotate-180" : ""}`} />
            </Button>
          </div>

          {/* Feature Comparison Table */}
          {showComparison && (
            <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">
                Full Feature Comparison
              </h3>
              <PlanComparisonTable 
                packages={subscriptionPackages}
                currencySymbol={currencySymbol}
                showPrices={true}
                highlightTier="standard"
              />
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Loved by businesses
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">just like yours</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {[
              {
                quote: "Rotalie has completely transformed how we manage our staff schedules. What used to take hours now takes minutes.",
                author: "Sarah Mitchell",
                role: "Restaurant Owner",
                image: "https://images.unsplash.com/photo-1713946598551-4b7b910cf4c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHdvbWFuJTIwcG9ydHJhaXQlMjBzbWlsaW5nJTIwb2ZmaWNlfGVufDB8fHx8MTc2OTk0NjQ3Mnww&ixlib=rb-4.1.0&q=85&w=200"
              },
              {
                quote: "The real-time cost tracking alone has saved us thousands. We finally have visibility into our labor costs.",
                author: "James Cooper",
                role: "Café Manager",
                image: "https://images.unsplash.com/photo-1656399910089-b7ead999bf23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1hbiUyMHBvcnRyYWl0JTIwc21pbGluZyUyMG9mZmljZXxlbnwwfHx8fDE3Njk5NDY0NzZ8MA&ixlib=rb-4.1.0&q=85&w=200"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 lg:p-10 border border-slate-200 shadow-sm">
                <p className="text-xl lg:text-2xl text-slate-700 leading-relaxed mb-8 font-medium">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.author}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 lg:py-24 px-6 lg:px-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Secure & Reliable</h3>
              <p className="text-slate-600 leading-relaxed">Your data is encrypted and backed up daily. 99.9% uptime guaranteed.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Lightning Fast</h3>
              <p className="text-slate-600 leading-relaxed">Instant updates, real-time sync. No more waiting for your schedule to load.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Dedicated Support</h3>
              <p className="text-slate-600 leading-relaxed">Our team is here to help you succeed. Email support included on all plans.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8 bg-slate-50" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Frequently asked
              <br />
              <span className="bg-gradient-to-r from-violet-600 to-teal-500 bg-clip-text text-transparent">questions</span>
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to know about Rotalie
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-2xl border border-slate-200 px-6 data-[state=open]:shadow-lg transition-all duration-300"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-6 text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-6 text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-8 bg-gradient-to-br from-violet-600 to-teal-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Ready to simplify
            <br />
            your scheduling?
          </h2>
          <p className="text-lg lg:text-xl text-white/80 mb-10 max-w-xl mx-auto">
            Join thousands of businesses using Rotalie. Start your free trial today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup">
              <Button className="bg-white hover:bg-slate-50 text-violet-600 rounded-full px-8 py-6 text-lg shadow-xl shadow-black/10 transition-all duration-300 font-semibold" data-testid="cta-signup-btn">
                Get started free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-white/60 text-sm mt-6">No credit card required • Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <RotalieLogoLight />
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Rotalie. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
